import React from 'react'
import styled from 'styled-components'
import { OfficeTeikiKenkoshindanJoseikinData } from '../../../../types/formData'
import { Heading } from '../../../ui/Heading'
import { Input } from '../../../ui/Input'
import { DatePicker } from '../../../ui/DatePicker'
import { Controller, useFormContext } from 'react-hook-form'
import {
  BankAccountSection,
  FileSection,
  OfficeInfoSection,
  ApplicantSection,
} from './inputParts'
import { MessageColumnSection } from '../../../layout/inputParts/MessageColumnSection'
import { mediaQuery } from '../../../../themes/size'
import { isYYYYMMDD } from '../../../../util/validation'

type Props = {
  disabled: boolean
  canAdminUpload?: boolean
  handleSaveImage?: (files: File) => Promise<void>
  isUploading?: boolean
  handleDeleteImage?: (fileId: string) => Promise<void>
  shouldShowOtherFormLinks?: true
}

const Component: React.FC<Props> = (props) => {
  const methods = useFormContext<
    OfficeTeikiKenkoshindanJoseikinData['formData']
  >()
  const { control, errors } = methods

  return (
    <>
      <HeadingArea>
        <Heading type="screenTitle" tag="h1">
          ▼定期健康診断助成金請求書
        </Heading>
      </HeadingArea>

      {Object.keys(methods.errors).length > 0 && (
        <Section>
          <ErrorMessage>
            入力内容に不備があります。各入力項目のメッセージを参照してください。
          </ErrorMessage>
        </Section>
      )}

      <OfficeInfoSection
        shouldShowOtherFormLinks={props.shouldShowOtherFormLinks}
      />

      <ApplicantSection disabled={props.disabled} />

      <Section>
        <Heading type="sectionTitle" tag="h2">
          ■申請情報
        </Heading>
        <InputField>
          <Heading type="blockTitle" tag="span">
            利用施設名
          </Heading>
          <Controller
            control={control}
            name="facilityName"
            rules={{ required: '利用施設名を入力してください' }}
            render={({ onChange, value, onBlur }) => (
              <Input
                type="text"
                defaultValue={value}
                onChange={onChange}
                disabled={props.disabled}
                onBlurCapture={onBlur}
              />
            )}
          />
        </InputField>
        {errors.facilityName && (
          <ErrorMessage>{errors.facilityName.message}</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            利用人数
          </Heading>
          <LabelWrapper>
            <Controller
              control={control}
              name="numOfUsers"
              rules={{ required: '利用人数を入力してください' }}
              render={({ onChange, value, onBlur }) => (
                <Input
                  type="number"
                  min="0"
                  defaultValue={value}
                  onChange={(e) => {
                    onChange(e)
                    const num = methods.watch('numOfUsers')
                    methods.setValue(
                      'amount',
                      new Intl.NumberFormat().format(num * 2000),
                    )
                  }}
                  disabled={props.disabled}
                  onBlurCapture={onBlur}
                />
              )}
            />
            <InputLabel>人</InputLabel>
          </LabelWrapper>
        </InputField>
        {errors.numOfUsers && (
          <ErrorMessage>{errors.numOfUsers.message}</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            利用年月日
            <br />
            (最初に受診された日)
          </Heading>
          <Controller
            control={control}
            name="firstUseDate"
            rules={{
              required: '最初に受診された日を入力してください',
              validate: { isYYYYMMDD },
            }}
            render={({ onChange, value, onBlur }) => (
              <DatePicker
                onChange={onChange}
                value={value}
                disabled={props.disabled}
                onBlur={onBlur}
              />
            )}
          />
        </InputField>
        {errors.firstUseDate && (
          <ErrorMessage>{errors.firstUseDate.message}</ErrorMessage>
        )}
        {errors.firstUseDate?.type === 'isYYYYMMDD' && (
          <ErrorMessage>YYYY/MM/DDの形式で入力して下さい</ErrorMessage>
        )}
        <InputField>
          <Heading type="blockTitle" tag="span">
            請求金額
          </Heading>
          <LabelWrapper>
            <Controller
              control={control}
              name="amount"
              defaultValue="0"
              render={({ value }) => (
                <Input type="text" value={value} disabled />
              )}
            />
            <InputLabel>円</InputLabel>
          </LabelWrapper>
        </InputField>
      </Section>

      <FileSection
        headingLabel="病院発行の領収書・受診者名簿"
        disabled={props.disabled}
        canAdminUpload={props.canAdminUpload}
        handleSaveImage={props.handleSaveImage}
        isUploading={props.isUploading}
        handleDeleteImage={props.handleDeleteImage}
      />

      <BankAccountSection disabled={props.disabled} />

      <MessageColumnSection disabled={props.disabled} />
    </>
  )
}

const HeadingArea = styled.div`
  margin-top: 32px;
`
export const Section = styled.section`
  margin-top: 72px;
`
export const InputField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 510px;
  margin-top: 16px;

  @media screen and (max-width: ${mediaQuery.SP}px) {
    display: block;
    width: 100%;

    & > span {
      &:first-child {
        margin-bottom: 8px;
      }
    }
  }
`
const ErrorMessage = styled.div`
  color: red;
`
const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 328px;
`
const InputLabel = styled.label`
  margin-left: 8px;
  font-size: 16px;
`

export default Component
